import { z } from 'zod';

const bool = z
  .enum(['true', 'false', ''])
  .default('false')
  .transform((v) => v === 'true');

const schema = z.object({
  NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
  /** A TCP port, or (on cPanel/Passenger hosting) the socket path the host hands us. */
  PORT: z
    .union([z.coerce.number().int().positive(), z.string().min(1)])
    .transform((v) => (typeof v === 'number' || /^d+$/.test(v) ? Number(v) : v))
    .default(4000),
  LOG_LEVEL: z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace', 'silent']).default('info'),
  DATABASE_URL: z.string().min(1, 'DATABASE_URL is required'),
  APP_URL: z.string().url().default('http://localhost:5173'),
  /** Absolute path to the built web app (web/dist). When set, this server also serves the
   * frontend and falls back to index.html for client-side routes, so one Node app is enough
   * on hosts (like cPanel) that give you a single process per domain. Leave unset when a
   * separate web server (nginx, Apache) serves web/dist, as on the VPS deployment path. */
  WEB_DIST_DIR: z.string().optional(),
  /** Optional Cloudflare Turnstile keys for the public lead form (a built-in time-token check is used without them). */
  TURNSTILE_SITE_KEY: z.string().optional(),
  TURNSTILE_SECRET: z.string().optional(),
  /** Optional WhatsApp Business Cloud API (automatic sending with delivery status). Off unless all are set. */
  WHATSAPP_CLOUD_TOKEN: z.string().optional(),
  WHATSAPP_PHONE_NUMBER_ID: z.string().optional(),
  WHATSAPP_VERIFY_TOKEN: z.string().optional(),
  WHATSAPP_APP_SECRET: z.string().optional(),
  /** Optional Web Push (phone notifications). Generate with: npx web-push generate-vapid-keys */
  VAPID_PUBLIC_KEY: z.string().optional(),
  VAPID_PRIVATE_KEY: z.string().optional(),
  VAPID_SUBJECT: z.string().default('mailto:admin@localhost'),
  CORS_ORIGINS: z
    .string()
    .default('http://localhost:5173')
    .transform((v) =>
      v
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  JWT_ACCESS_SECRET: z.string().min(32, 'JWT_ACCESS_SECRET must be at least 32 characters'),
  ENCRYPTION_KEY: z
    .string()
    .refine(
      (v) => Buffer.from(v, 'base64').length === 32,
      'ENCRYPTION_KEY must be 32 bytes, base64',
    ),
  CRON_SECRET: z.string().min(12).default('change-me-change-me'),
  /** Max API requests per IP per minute across the whole API. */
  API_RATE_LIMIT: z.coerce.number().int().positive().default(600),
  /** Max sign-in attempts per IP per 15 minutes (raised only for automated browser tests). */
  AUTH_RATE_LIMIT: z.coerce.number().int().positive().default(20),
  MAIL_FROM: z.string().default('Roovers Business Suite <billing@localhost>'),
  SMTP_HOST: z.string().default(''),
  SMTP_PORT: z.coerce.number().int().default(587),
  SMTP_SECURE: bool,
  SMTP_USER: z.string().default(''),
  SMTP_PASS: z.string().default(''),
});

export type Config = z.infer<typeof schema>;

export function loadConfig(env: NodeJS.ProcessEnv = process.env): Config {
  const parsed = schema.safeParse(env);
  if (!parsed.success) {
    const issues = parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ');
    throw new Error(`Invalid environment configuration: ${issues}`);
  }
  return parsed.data;
}

/** Test/dev helper: a complete valid config with throwaway secrets. */
export const TEST_ENV: NodeJS.ProcessEnv = {
  NODE_ENV: 'test',
  DATABASE_URL: 'mysql://x:y@localhost:3306/z',
  CORS_ORIGINS: 'http://localhost:5173',
  JWT_ACCESS_SECRET: 'test-access-secret-test-access-secret-1234',
  ENCRYPTION_KEY: Buffer.alloc(32, 7).toString('base64'),
  CRON_SECRET: 'test-cron-secret-123',
};
